import { useState } from "react";
// @mui core components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "src/components/SoftBox";
import SoftTypography from "src/components/SoftTypography";

import { useClubs } from "src/features/club/ClubProvider";
import { getClubLocationsCollection } from "src/features/club/collections";

import { doc, updateDoc } from "firebase/firestore";
import Switch from "@mui/material/Switch";
import SoftCurrencyInput from "src/components/SoftCurrencyInput";
import SoftCurrencyInputv2 from "src/components/SoftCurrencyInput/SoftCurrencyInputv2";
import SoftSelect from "src/components/SoftSelect";

function FuelReimbursement() {
  const { selectedClubId, selectedLocation } = useClubs();
  const { preferences } = selectedLocation;
  const [reimbursementType, setReimbursementType] = useState(
    preferences?.fuelReimbursement?.type || {}
  );

  const updatePreference = async (key, value) => {
    preferences[key] = value;
    try {
      await updateDoc(
        doc(getClubLocationsCollection(selectedClubId), selectedLocation.id),
        {
          preferences,
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const toggleFuelReimbursement = (event) => {
    updatePreference("fuelReimbursement", {
      ...preferences.fuelReimbursement,
      enabled: event.target.checked,
      amount: preferences?.fuelReimbursement?.amount ?? 0,
    });
  };

  const toggleForceFuelReceipt = (event) => {
    updatePreference("fuelReimbursement", {
      ...preferences.fuelReimbursement,
      forceReceipt: event.target.checked,
    });
  };

  const onChangeFuelReimbursementType = (event) => {
    setReimbursementType(event);
    updatePreference("fuelReimbursement", {
      ...preferences.fuelReimbursement,
      type: event,
    });
  };

  const handleFuelReimbursementRateChange = (valueInCents) => {
    let rate = Number(valueInCents);
    if (!rate || Number.isNaN(rate)) rate = 0;

    updatePreference("fuelReimbursement", {
      ...preferences.fuelReimbursement,
      rate,
    });
  };

  const handleMaxFuelReimbursementChange = (valueInCents) => {
    let rate = Number(valueInCents);
    if (!rate || Number.isNaN(rate)) rate = 0;

    updatePreference("fuelReimbursement", {
      ...preferences.fuelReimbursement,
      maxAmount: rate,
    });
  };

  const handleMaxGallonsFuelReimbursementChange = (value) => {
    console.log("value", value);
    let gallons = Number(value);
    if (!gallons || Number.isNaN(gallons)) gallons = 0;

    updatePreference("fuelReimbursement", {
      ...preferences.fuelReimbursement,
      maxGallons: gallons,
    });
  };

  return (
    <Card
      id="fuel-reimbursement"
      sx={{
        overflow: "visible",
      }}
    >
      <SoftBox p={3} lineHeight={1}>
        <SoftBox mb={1}>
          <SoftTypography variant="h5">Fuel Reimbursement</SoftTypography>
        </SoftBox>
        <SoftTypography variant="button" color="text" fontWeight="regular">
          Settings for fuel reimbursement when renting aircraft wet
        </SoftTypography>
      </SoftBox>

      <SoftBox p={3} pb={1} lineHeight={1}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <SoftBox lineHeight={1.4}>
            <SoftTypography
              display="block"
              variant="button"
              fontWeight="medium"
            >
              Enable Fuel Reimbursement
            </SoftTypography>
            <SoftTypography variant="caption" color="text" fontWeight="regular">
              If enabled, pilots can add fuel reimbursements when checking in
              aircraft
            </SoftTypography>
          </SoftBox>
          <SoftBox ml={2} mr={1}>
            <Switch
              checked={preferences?.fuelReimbursement?.enabled ?? false}
              onChange={toggleFuelReimbursement}
            />
          </SoftBox>
        </SoftBox>
      </SoftBox>

      {preferences?.fuelReimbursement?.enabled && (
        <>
          <SoftBox p={3} pb={1} lineHeight={1}>
            <SoftBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <SoftBox lineHeight={1.4}>
                <SoftTypography
                  display="block"
                  variant="button"
                  fontWeight="medium"
                >
                  Force Receipt Upload
                </SoftTypography>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="regular"
                >
                  If enabled, a recipt must be uploaded when adding a fuel
                  reimbursement
                </SoftTypography>
              </SoftBox>
              <SoftBox ml={2} mr={1}>
                <Switch
                  checked={
                    preferences?.fuelReimbursement?.forceReceipt ?? false
                  }
                  onChange={toggleForceFuelReceipt}
                />
              </SoftBox>
            </SoftBox>
          </SoftBox>

          <SoftBox p={3} pb={1} lineHeight={1}>
            <SoftBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <SoftBox lineHeight={1.4}>
                <SoftTypography
                  display="block"
                  variant="button"
                  fontWeight="medium"
                >
                  Reimbursement Type
                </SoftTypography>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="regular"
                >
                  Set how the reimbursement amount is collected at time of
                  aircraft check in
                </SoftTypography>
              </SoftBox>
              <SoftBox ml={2} mr={1} width={300}>
                <SoftSelect
                  options={[
                    {
                      label: "Gallons",
                      value: "gallons",
                    },
                    {
                      label: "Dollars",
                      value: "dollar",
                    },
                  ]}
                  value={reimbursementType}
                  onChange={(e) => onChangeFuelReimbursementType(e)}
                  label="Reimbursement Type"
                />
              </SoftBox>
            </SoftBox>
          </SoftBox>

          {reimbursementType.value === "dollar" ? (
            <SoftBox pl={3} pr={3} pb={3} lineHeight={1}>
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                lineHeight={1}
              >
                <SoftBox lineHeight={1.4}>
                  <SoftTypography
                    display="block"
                    variant="button"
                    fontWeight="medium"
                  >
                    Max Allowed Amount Per Gallon
                  </SoftTypography>
                  <SoftTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    Enter a maximum amount per gallon that can be reimbursed.
                    Enter $0 for no limit on reimbursement amounts.
                  </SoftTypography>
                </SoftBox>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                  lineHeight={1}
                >
                  <SoftBox ml={1} width={80}>
                    <SoftCurrencyInputv2
                      currencySymbol="$"
                      placeholder="0"
                      decimalPlaces={2}
                      outputFormat="number"
                      value={preferences?.fuelReimbursement?.maxAmount ?? 0}
                      onBlurChangedValue={({ valueInCents }) => {
                        handleMaxFuelReimbursementChange(valueInCents);
                      }}
                      allowValueUpdate
                    />
                  </SoftBox>
                </SoftBox>
              </SoftBox>
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                lineHeight={1}
              >
                <SoftBox lineHeight={1.4}>
                  <SoftTypography
                    display="block"
                    variant="button"
                    fontWeight="medium"
                  >
                    Max Allowed Gallons
                  </SoftTypography>
                  <SoftTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    Enter a maximum amount of gallons that can be reimbursed in
                    a single booking. Enter 0 for no limit on amount of gallons.
                  </SoftTypography>
                </SoftBox>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                  lineHeight={1}
                >
                  <SoftBox ml={1} width={80}>
                    <SoftCurrencyInput
                      currencySymbol=""
                      placeholder="0"
                      decimalPlaces={0}
                      decimalPlacesShownOnBlur={0}
                      decimalPlacesShownOnFocus={0}
                      outputFormat="number"
                      value={preferences?.fuelReimbursement?.maxGallons ?? 0}
                      onBlur={(event) =>
                        handleMaxGallonsFuelReimbursementChange(
                          event.target.value
                        )
                      }
                      allowValueUpdate
                    />
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          ) : (
            <SoftBox pl={3} pr={3} pb={3} lineHeight={1}>
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                lineHeight={1}
              >
                <SoftBox lineHeight={1.4}>
                  <SoftTypography
                    display="block"
                    variant="button"
                    fontWeight="medium"
                  >
                    Reimbursement Amount
                  </SoftTypography>
                  <SoftTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    Enter an amount to be reimbursed per gallon of fuel
                  </SoftTypography>
                </SoftBox>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                  lineHeight={1}
                >
                  <SoftBox ml={1} width={80}>
                    <SoftCurrencyInputv2
                      currencySymbol="$"
                      placeholder="0"
                      decimalPlaces={2}
                      outputFormat="number"
                      value={preferences?.fuelReimbursement?.rate ?? 0}
                      onBlurChangedValue={({ valueInCents }) => {
                        handleFuelReimbursementRateChange(valueInCents);
                      }}
                      allowValueUpdate
                    />
                  </SoftBox>
                  <SoftBox ml={1} mr={1}>
                    <SoftTypography
                      variant="caption"
                      color="text"
                      fontWeight="regular"
                    >
                      per gallon
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          )}
        </>
      )}
    </Card>
  );
}

export default FuelReimbursement;
